/**
 * VÉRAULT Portal — PexelsImagePicker (Phase 2 · Pass 1.5)
 *
 * Inline modal/drawer for searching Pexels and picking a photo URL.
 * Used by ExperienceItem editor + ProposalBuilder cover image.
 *
 * Calls the secure backend proxy (`/api/images/search`) — the Pexels API
 * key never reaches the browser.
 *
 * Image standards (per brand brief):
 *   · Landscape · Large size by default · editorial photography
 *   · Photographer credit + Pexels link displayed inline
 *   · Advisor manually picks — never auto-applied
 */
import React from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Search, Loader2, ImagePlus, ExternalLink, Check } from "lucide-react";
import { toast } from "sonner";
import { PortalButton, api } from "../index";
import useBodyScrollLock from "./useBodyScrollLock";

export default function PexelsImagePicker({ open, defaultQuery = "", onClose, onPick }) {
  const [query, setQuery] = React.useState(defaultQuery);
  const [photos, setPhotos] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [total, setTotal] = React.useState(0);
  const [picked, setPicked] = React.useState(null);

  // Wave 2 · Priority 3 — paste-URL tab
  const [mode, setMode] = React.useState("pexels"); // "pexels" | "url"
  const [pastedUrl, setPastedUrl] = React.useState("");
  const [urlPreview, setUrlPreview] = React.useState(null);
  const [urlError, setUrlError] = React.useState(null);
  const [urlLoading, setUrlLoading] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setQuery(defaultQuery || "");
      setMode("pexels");
      setPastedUrl("");
      setUrlPreview(null);
      setUrlError(null);
    }
  }, [open, defaultQuery]);

  const search = async (q = query, p = 1) => {
    if (!q || !q.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.get("/images/search", {
        params: { query: q.trim(), page: p, per_page: 20, orientation: "landscape", size: "large" },
      });
      setPhotos(p === 1 ? data.photos : [...photos, ...data.photos]);
      setPage(data.page || p);
      setTotal(data.total_results || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reach Pexels.");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (open && defaultQuery) {
      setQuery(defaultQuery);
      search(defaultQuery, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultQuery]);

  const choose = () => {
    if (mode === "url") {
      if (!urlPreview) return;
      onPick && onPick({
        url: urlPreview.url,
        thumbnail_url: urlPreview.url,
        photographer: null,
        photographer_url: null,
        pexels_url: null,
        alt: "",
        source: "url",
      });
      onClose && onClose();
      return;
    }
    if (!picked) return;
    onPick && onPick({
      url: picked.large_url,
      thumbnail_url: picked.thumbnail_url,
      photographer: picked.photographer,
      photographer_url: picked.photographer_url,
      pexels_url: picked.pexels_url,
      alt: picked.alt,
      source: "pexels",
    });
    setPicked(null);
    onClose && onClose();
  };

  // Validate the pasted URL by loading it as an image (browser-native).
  const tryUrl = () => {
    const u = (pastedUrl || "").trim();
    if (!u) return;
    if (!/^https?:\/\//i.test(u)) {
      setUrlError("URL must start with http:// or https://");
      setUrlPreview(null);
      return;
    }
    setUrlError(null);
    setUrlPreview(null);
    setUrlLoading(true);
    const img = new Image();
    img.onload = () => {
      setUrlLoading(false);
      setUrlPreview({ url: u });
    };
    img.onerror = () => {
      setUrlLoading(false);
      setUrlError("Couldn't load that image. Try a direct image URL (.jpg / .png / .webp).");
    };
    img.src = u;
  };

  // Wave 1 · Priority 7 — lock body scroll while the modal is open so the
  // user can't lose the picker mid-scroll, then render via portal to body
  // so we escape any transformed/overflow-hidden ancestor.
  React.useEffect(() => {
    if (!open || typeof document === "undefined") return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const tree = (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center px-2 sm:px-4 py-4 sm:py-8">
          <motion.div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            data-testid="pexels-picker-overlay"
          />
          <motion.div
            className="relative w-full sm:max-w-[920px] max-h-[calc(100vh-2rem)] sm:max-h-[90vh] bg-[hsl(var(--background))] rounded-xl shadow-2xl flex flex-col overflow-hidden"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            data-testid="pexels-picker"
          >
            <div className="px-5 py-4 border-b border-[rgba(0,0,0,0.08)] flex items-center justify-between gap-3 flex-wrap shrink-0">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">Image picker</p>
                <h2 className="font-serif text-[17px]">Pexels · landscape · editorial</h2>
              </div>
              <button onClick={onClose} className="p-1.5 rounded hover:bg-[hsl(var(--background-paper))]" data-testid="pexels-picker-close">
                <X className="size-4" strokeWidth={1.5} />
              </button>
            </div>

            <div className="px-5 py-3 border-b border-[rgba(0,0,0,0.05)] flex items-center gap-2 shrink-0">
              <div className="flex items-center bg-[hsl(var(--background-paper))]/50 rounded-full p-0.5">
                <button
                  type="button"
                  onClick={() => setMode("pexels")}
                  className={`px-3 py-1 rounded-full font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${mode === "pexels" ? "bg-white text-[hsl(var(--foreground))]" : "text-[hsl(var(--foreground-muted))]"}`}
                  data-testid="pexels-picker-tab-pexels"
                >
                  Search Pexels
                </button>
                <button
                  type="button"
                  onClick={() => setMode("url")}
                  className={`px-3 py-1 rounded-full font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${mode === "url" ? "bg-white text-[hsl(var(--foreground))]" : "text-[hsl(var(--foreground-muted))]"}`}
                  data-testid="pexels-picker-tab-url"
                >
                  Paste URL
                </button>
              </div>
              {mode === "pexels" && (
                <>
                  <Search className="size-4 text-[hsl(var(--foreground-muted))] shrink-0 ml-2" strokeWidth={1.5} />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(query, 1); } }}
                    placeholder="Search e.g. 'Lisbon luxury hotel', 'Algarve coast', 'Sintra'"
                    className="flex-1 min-w-0 px-3 py-2 rounded-md border border-[rgba(0,0,0,0.08)] bg-white text-[14px] font-serif"
                    data-testid="pexels-picker-input"
                    autoFocus
                  />
                  <PortalButton variant="primary" size="sm" onClick={() => search(query, 1)} disabled={loading || !query.trim()} icon={loading ? <Loader2 className="size-3.5 animate-spin" /> : <Search className="size-3.5" />} data-testid="pexels-picker-search">
                    {loading ? "Searching" : "Search"}
                  </PortalButton>
                </>
              )}
              {mode === "url" && (
                <>
                  <input
                    type="url"
                    value={pastedUrl}
                    onChange={(e) => setPastedUrl(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); tryUrl(); } }}
                    placeholder="https://example.com/photo.jpg"
                    className="flex-1 min-w-0 px-3 py-2 rounded-md border border-[rgba(0,0,0,0.08)] bg-white text-[14px] font-serif"
                    data-testid="pexels-picker-url-input"
                  />
                  <PortalButton variant="primary" size="sm" onClick={tryUrl} disabled={!pastedUrl.trim() || urlLoading} icon={urlLoading ? <Loader2 className="size-3.5 animate-spin" /> : <Check className="size-3.5" />} data-testid="pexels-picker-url-validate">
                    {urlLoading ? "Loading" : "Preview"}
                  </PortalButton>
                </>
              )}
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
              {mode === "url" && (
                <div className="space-y-4" data-testid="pexels-picker-url-body">
                  <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">
                    Paste a direct image URL — the system loads it to confirm the link actually resolves before saving. Use direct image links (.jpg, .png, .webp), not page URLs.
                  </p>
                  {urlError && (
                    <div className="px-3 py-2 rounded-md bg-red-50 border border-red-100 text-red-700 font-serif text-[13.5px]" data-testid="pexels-picker-url-error">
                      {urlError}
                    </div>
                  )}
                  {urlPreview && (
                    <div className="space-y-2" data-testid="pexels-picker-url-preview">
                      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--accent-gold))]">Preview</p>
                      <img src={urlPreview.url} alt="" className="w-full max-h-[420px] object-contain rounded-md border border-[rgba(0,0,0,0.05)]" />
                    </div>
                  )}
                </div>
              )}
              {mode === "pexels" && photos.length === 0 && !loading && (
                <p className="text-center font-accent italic text-[13px] text-[hsl(var(--foreground-muted))] py-12">
                  Search above to find an editorial image. Try the destination, hotel, or vibe word — Pexels rewards specific phrasing.
                </p>
              )}
              {mode === "pexels" && photos.length === 0 && loading && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="size-5 animate-spin text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
                </div>
              )}
              {mode === "pexels" && photos.length > 0 && (
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] mb-3">
                  {total.toLocaleString()} results · click to select · landscape · large
                </p>
              )}
              {mode === "pexels" && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {photos.map((p) => {
                    const isPicked = picked && picked.id === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setPicked(p)}
                        className={`group relative overflow-hidden rounded-md ring-2 transition-all ${isPicked ? "ring-[hsl(var(--accent-gold))]" : "ring-transparent hover:ring-[hsl(var(--accent-gold))]/40"}`}
                        data-testid={`pexels-pick-${p.id}`}
                      >
                        <img src={p.thumbnail_url} alt={p.alt || ""} className="w-full h-32 object-cover" loading="lazy" />
                        {isPicked && (
                          <div className="absolute inset-0 bg-[hsl(var(--accent-gold))]/10 flex items-center justify-center">
                            <Check className="size-6 text-[hsl(var(--accent-gold))]" strokeWidth={2} />
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-black/45 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                          <p className="font-mono text-[9px] uppercase tracking-[0.18em] truncate">
                            © {p.photographer}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {mode === "pexels" && photos.length > 0 && photos.length < total && (
                <div className="mt-4 text-center">
                  <PortalButton variant="ghost" size="sm" onClick={() => search(query, page + 1)} disabled={loading} data-testid="pexels-picker-more">
                    {loading ? "Loading…" : "Load more"}
                  </PortalButton>
                </div>
              )}
            </div>

            <div className="px-5 py-3 border-t border-[rgba(0,0,0,0.05)] flex items-center justify-between gap-3 flex-wrap shrink-0">
              {mode === "pexels" && picked && (
                <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-secondary))] truncate min-w-0">
                  Selected — photo by{" "}
                  <a href={picked.photographer_url} target="_blank" rel="noreferrer noopener" className="underline hover:text-[hsl(var(--accent-gold))]">
                    {picked.photographer}
                  </a>{" "}
                  on{" "}
                  <a href={picked.pexels_url} target="_blank" rel="noreferrer noopener" className="underline hover:text-[hsl(var(--accent-gold))]">
                    Pexels <ExternalLink className="inline size-2.5" strokeWidth={1.5} />
                  </a>
                </p>
              )}
              {mode === "pexels" && !picked && (
                <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">Tap an image to select.</p>
              )}
              {mode === "url" && urlPreview && (
                <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-secondary))] truncate min-w-0">
                  Image preview validated.
                </p>
              )}
              {mode === "url" && !urlPreview && (
                <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">Paste a direct image URL and press Preview.</p>
              )}
              <div className="flex items-center gap-2 shrink-0">
                <PortalButton variant="ghost" size="sm" onClick={onClose}>Cancel</PortalButton>
                <PortalButton
                  variant="primary"
                  size="sm"
                  onClick={choose}
                  disabled={mode === "pexels" ? !picked : !urlPreview}
                  icon={<ImagePlus className="size-3.5" strokeWidth={1.5} />}
                  data-testid="pexels-picker-apply"
                >
                  Use this image
                </PortalButton>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  if (typeof document === "undefined") return null;
  return createPortal(tree, document.body);
}
